/**
 * Reference tone playback, and the gate that keeps it out of the meters.
 *
 * Two responsibilities that cannot be separated: the moment a tone sounds, the
 * microphone hears it, so whoever starts the tone has to close the measurement
 * channel and reopen it at the right time.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { DEFAULT_PLAN_OPTIONS } from '../../audio/probe-plan'
import { ReferenceTonePlayer, type ReferenceTonePort } from '../../audio/reference-tone'
import { liveReadings } from '../../state/live-readings'
import { freqOf } from '../../music/pitch'
import type { GuitarString } from '../../music/tunings'
import { useTunerStore } from '../../state/tuner-store'

/**
 * How far back the tracker's memory still reaches, in milliseconds.
 *
 * Its probes average over a sliding window, so a reading taken just after a
 * tone has died away is still partly made of it. Reopening the channel the
 * instant the sound stops would let exactly the readings through that the
 * gate exists to prevent.
 */
export const TRACKER_MEMORY_MS = DEFAULT_PLAN_OPTIONS.maxWindowMs

/**
 * Extra slack on top of that window.
 *
 * Covers the low-pass settling behind it: measured worst case a string needs
 * 245 ms to hold within a cent, and the gate should outlast that.
 */
export const GATE_MARGIN_MS = 260

/** What the tuner bars need to play and show a reference tone. */
export interface ReferenceToneControls {
  /** Id of the string currently sounding, or null. */
  readonly playingId: string | null
  /** True while measurements are being discarded because of a tone. */
  readonly isMeasurementPaused: boolean
  /** Play the open-string pitch of a string in the current tuning. */
  readonly play: (string: GuitarString) => void
}

/** Injection point for tests; the default builds a real Web Audio player. */
export interface ReferenceToneHookOptions {
  readonly createPlayer?: () => ReferenceTonePort
}

/**
 * Play the reference tone of a string and keep the meters honest while it does.
 *
 * The concert pitch is read when the button is pressed rather than captured on
 * render, so the tone always matches what the meters measure against.
 * Transposition deliberately plays no part: it renames pitches, it does not
 * move them.
 */
export function useReferenceTone(options: ReferenceToneHookOptions = {}): ReferenceToneControls {
  const [playingId, setPlayingId] = useState<string | null>(null)
  const [isMeasurementPaused, setMeasurementPaused] = useState(false)
  const { createPlayer } = options
  const playerRef = useRef<ReferenceTonePort | null>(null)
  // Two deadlines, because they are genuinely different: the bar stops
  // pulsing when the string stops sounding, the meters stay shut until the
  // last analysis window containing the tone has passed through.
  const soundTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const gateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearTimers = useCallback(() => {
    if (soundTimerRef.current !== null) clearTimeout(soundTimerRef.current)
    if (gateTimerRef.current !== null) clearTimeout(gateTimerRef.current)
    soundTimerRef.current = null
    gateTimerRef.current = null
  }, [])

  const getPlayer = useCallback((): ReferenceTonePort => {
    playerRef.current ??= createPlayer ? createPlayer() : new ReferenceTonePlayer()
    return playerRef.current
  }, [createPlayer])

  const play = useCallback(
    (string: GuitarString) => {
      clearTimers()

      let ringSeconds: number
      try {
        // Started inside the click handler on purpose: Safari refuses an
        // AudioContext created after the gesture has passed.
        ringSeconds = getPlayer().play(freqOf(string.midi, useTunerStore.getState().a4))
      } catch (error) {
        liveReadings.setMuted(false)
        setPlayingId(null)
        setMeasurementPaused(false)
        console.error('[multitimbral] reference tone could not be played', error)
        return
      }

      liveReadings.setMuted(true)
      setPlayingId(string.id)
      setMeasurementPaused(true)

      const ringMs = ringSeconds * 1000
      soundTimerRef.current = setTimeout(() => {
        soundTimerRef.current = null
        setPlayingId(null)
      }, ringMs)

      gateTimerRef.current = setTimeout(
        () => {
          gateTimerRef.current = null
          liveReadings.setMuted(false)
          setMeasurementPaused(false)
        },
        ringMs + TRACKER_MEMORY_MS + GATE_MARGIN_MS,
      )
    },
    [clearTimers, getPlayer],
  )

  // Leaving the view with a tone still ringing would strand the mute flag and
  // leave the meters dead for good.
  useEffect(() => {
    return () => {
      clearTimers()
      liveReadings.setMuted(false)
      const player = playerRef.current
      playerRef.current = null
      void player?.close()
    }
  }, [clearTimers])

  return { playingId, isMeasurementPaused, play }
}
